import React, { useEffect, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import BaseCell from './CellComponents/BaseCell'
import useInfiniteScroll from './useInfiniteScroll'

import './Table.sass'

export interface ColumnDef {
  name: string
  component?: any
  [x: string]: any
}

export interface CellCompProps<T> {
  row: T
  col: ColumnDef
}

interface TableProps<T> {
  data: T[]
  name?: string
  className?: string
  columns?: ColumnDef[]
  pageSize?: number
  handleSelect?: (entry: T) => void
}

const Table = <T extends {}> (
  { data: allData = [], name = 'default', className, columns = [], pageSize = 20, handleSelect }: TableProps<T>
) => {
  const [pages, setPages] = useState(1)
  const next = useMemo(() => () => setPages(p => p + 1), [])
  const ref: any = useInfiniteScroll(next)
  const data = useMemo(() => allData.slice(0, pageSize * pages), [allData, pages])
  useEffect(() => {
    // On filter changes (or any other data change), reset pagination and scroll
    setPages(1)
    ref.current.scrollTop = 0
  }, [allData, ref])

  let cls = 'table-layout'
  if (className) cls += ' ' + className

  return (
    <div id={`table-${name}`} className={cls} ref={ref}>
      <header className="row">
        {columns.map(col =>
          <div key={col.name} className={`cell ${col}`}>
            <FormattedMessage id={`table.${name}.${col.name}`} />
          </div>
        )}
      </header>
      <section className="selected-data">
        {data.map((row, i) =>
          <div onClick={() => handleSelect && handleSelect(row)} key={i} className="row">
            {columns.map((col, j) => {
              const Comp = col.component || BaseCell
              return <Comp key={j} row={row} col={col} />
            })}
          </div>
        )}
      </section>
    </div>
  )
}

export default Table
